import { Suspense, lazy, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { Alert, Button } from '@mui/material';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import {
  type Property_Transactions_Bool_Exp,
  type Property_Transactions_Order_By,
} from '@graphql/__generated__/graphql';
import { ListToolbar } from '@modules/_shared/components/list-toolbar/ListToolbar';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { SmartListTitle } from '@modules/_shared/components/SmartListTitle';
import { TransactionsList } from '@modules/_shared/components/transactions/TransactionsList';
import { useBulkEditMode } from '@modules/_shared/hooks/useBulkEditMode';
import { useBulkPublishTransactions } from '@modules/_shared/hooks/useBulkPublishTransactions';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  getIncludedIds,
  isEmptySelection,
} from '@modules/_shared/utils/gridRowSelection';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import { prepareWhereClauseQuery } from '@modules/_shared/utils/parseWhereClause';
import { TEAM_USER_FILTER } from '@modules/_shared/utils/quickFilters';
import {
  GET_PROPERTY_TRANSACTIONS,
  GET_PROPERTY_TRANSACTIONS_LIST_COUNT,
} from '@modules/transactions/transactionQueries';
import { useLocale } from '@shared/hooks/locale';
import { lazyRetry } from '@shared/utils/lazyRetry';

const TransactionCreateDrawer = lazy(() =>
  lazyRetry(() => import('@modules/transactions/TransactionCreateDrawer')),
);
const TransactionDrawer = lazy(() =>
  lazyRetry(() => import('@modules/transactions/TransactionDrawer')),
);

export const Transactions = () => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const { me } = useAppData();

  const myTeamsIds = useMemo(
    () => me?.teams?.map(team => team.team_id) ?? [],
    [me?.teams],
  );
  const { editMode, selectedRows, setSelectedRows } = useBulkEditMode();
  const includedSelectedRows = getIncludedIds(selectedRows);

  const where: Property_Transactions_Bool_Exp = useMemo(
    () =>
      prepareWhereClauseQuery(JSON.parse(searchParams.get('where') || '{}')),
    [searchParams],
  );

  const order_by: Property_Transactions_Order_By = useMemo(
    () =>
      JSON.parse(
        searchParams.get('order_by') ??
          '[{ "signature_date": "desc_nulls_last" }, { "id": "desc" }]',
      ),
    [searchParams],
  );

  const { limit, offset } = getPaginationVariables(searchParams);

  const navigate = useNavigate();

  const transactionLink = (id: string) => ({
    pathname: `/transactions/${id}`,
    search: searchParams.toString(),
  });

  const variables = useMemo(
    () => ({
      where,
      order_by,
      limit,
      offset,
      team_ids: myTeamsIds,
      includeValuations: me?.can_edit_settings ?? false,
    }),
    [where, order_by, limit, offset, myTeamsIds, me?.can_edit_settings],
  );

  const { data, loading, error } = useQuery(GET_PROPERTY_TRANSACTIONS, {
    variables,
  });

  const { data: countData } = useQuery(GET_PROPERTY_TRANSACTIONS_LIST_COUNT, {
    variables: {
      where: variables.where,
    },
  });

  const refetchQueries = useMemo(
    () => [{ query: GET_PROPERTY_TRANSACTIONS, variables }],
    [variables],
  );

  const refetchQueriesWithCount = useMemo(
    () => [
      ...refetchQueries,
      {
        query: GET_PROPERTY_TRANSACTIONS_LIST_COUNT,
        variables: { where: variables.where },
      },
    ],
    [refetchQueries, variables.where],
  );

  const {
    apolloError,
    setErrorLike,
    skippedMessage,
    setSkippedMessage,
    bulkPublishLoading,
    bulkUnpublishLoading,
    bulkUpdateLoading,
    handleBulkPublish,
  } = useBulkPublishTransactions({
    selectedRows,
    setSelectedRows,
    refetchQueries,
  });

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  return (
    <>
      <SmartListTitle
        tableName="property_transactions"
        defaultTitle={t('Transactions')}
      />
      <ListToolbar
        tableName="property_transactions"
        quickFilters={[
          {
            label: t('Signature date'),
            path: ['property_transactions_bool_exp', 'signature_date'],
          },
          {
            label: t('Predicted signature date'),
            path: [
              'property_transactions_bool_exp',
              'estimated_signature_date',
            ],
          },
          {
            label: t('Payment date'),
            path: ['property_transactions_bool_exp', 'payment_date'],
          },
          {
            label: t('Seller agent'),
            path: ['property_transactions_bool_exp', 'seller_broker'],
            filter: TEAM_USER_FILTER,
          },
          {
            label: t('Seller team'),
            path: ['property_transactions_bool_exp', 'seller_broker_team'],
          },
          {
            label: t('Buyer agent'),
            path: ['property_transactions_bool_exp', 'buyer_broker'],
            filter: TEAM_USER_FILTER,
          },
          {
            label: t('Buyer team'),
            path: ['property_transactions_bool_exp', 'buyer_broker_team'],
          },
          {
            label: t('Uploaded by'),
            path: ['property_transactions_bool_exp', 'creator'],
            filter: TEAM_USER_FILTER,
          },
          {
            label: t('Seller'),
            path: [
              'property_transactions_bool_exp',
              'lot',
              'sellers',
              'seller',
            ],
          },
          {
            label: t('Place'),
            path: ['property_transactions_bool_exp', 'property', 'places'],
          },
          {
            label: t('Purchase price'),
            path: ['property_transactions_bool_exp', 'purchase_price'],
          },
          {
            label: t('Source'),
            path: [
              'property_transactions_bool_exp',
              'lot',
              'origin',
              'id',
              '_in',
            ],
            displayedColumn: 'label',
            filter: {
              path: ['name', 'type', '_in'],
              value: 'lot_origin_types',
            },
          },
          {
            label: t('Approved'),
            path: ['property_transactions_bool_exp', 'reviewed_at'],
            nullCheckMode: 'not-null',
          },
          {
            label: t('Published'),
            path: ['property_transactions_bool_exp', 'is_published'],
          },
        ]}
        newLink={{
          pathname: 'new',
          search: searchParams.toString(),
        }}
        bulkActions={
          me?.is_admin && (
            <>
              <Button
                disableElevation
                disabled={
                  isEmptySelection(selectedRows) ||
                  includedSelectedRows == null ||
                  bulkUpdateLoading
                }
                loading={bulkPublishLoading}
                loadingPosition="start"
                onClick={() => handleBulkPublish(true)}
              >
                {t('Publish')}
              </Button>
              <Button
                disableElevation
                disabled={
                  isEmptySelection(selectedRows) ||
                  includedSelectedRows == null ||
                  bulkUpdateLoading
                }
                loading={bulkUnpublishLoading}
                loadingPosition="start"
                onClick={() => handleBulkPublish(false)}
              >
                {t('Unpublish')}
              </Button>
            </>
          )
        }
      />
      <TransactionsList
        transactions={data?.property_transactions}
        loading={loading}
        displayPropertyInfo
        showReviews
        checkboxSelection={editMode}
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={setSelectedRows}
        extraDataGridProps={{
          rowCount:
            countData?.property_transactions_aggregate?.aggregate?.count ?? 0,
          onRowClick: ({ id }, e) => {
            if (id === 'auto-generated-group-footer-root') {
              return;
            } else if (id != null) {
              if (e.metaKey || e.ctrlKey) {
                window.open(transactionLink(id as string).pathname, '_blank');
                return;
              }
              navigate(transactionLink(id as string));
            }
          },
          initialState: {
            aggregation: {
              model: {
                purchase_price: 'sum',
                'commissions_aggregate.sum.amount': 'sum',
                'my_commissions.aggregate.sum.amount': 'sum',
              },
            },
          },
        }}
      />
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
      {skippedMessage != null && (
        <RASnackbar
          message={skippedMessage}
          severity="warning"
          onClose={() => setSkippedMessage(null)}
        />
      )}
      <Suspense fallback={null}>
        <Routes>
          <Route
            path="new/*"
            element={
              <TransactionCreateDrawer
                onCreate={(id: string) => {
                  navigate({
                    pathname: `${id}/transaction`,
                    search: searchParams.toString(),
                  });
                }}
                refetchQueries={refetchQueriesWithCount}
              />
            }
          />
          <Route
            path=":transactionId/*"
            element={<TransactionDrawer refetchQueries={refetchQueries} />}
          />
        </Routes>
      </Suspense>
    </>
  );
};

export default Transactions;
